import { MutableRefObject, useRef } from 'react'
import Swiper, { Swiper as SwiperType } from 'swiper'
import { Navigation } from 'swiper/modules'
import { useIsomorphicLayoutEffect } from '../../../../core/hook/useIsomorphicLayout'
import { AssetType } from '../ProductAssets.types'
import { handleError } from '../../../../core/hook/utils'
import Panzoom, { PanzoomObject } from '@panzoom/panzoom'

type PanzoomInstance = {
  panzoom: PanzoomObject
  wheelHandler: EventListener
  zoomHandler: EventListener
}

const showOrHideZoomControls = (asset: AssetType) => {
  const zoomControlsEl = document.querySelector('.product-assets-fullscreen-zoom-controls')
  if (asset.type === 'video') {
    zoomControlsEl?.classList.add('hide')
  } else {
    zoomControlsEl?.classList.remove('hide')
  }
}

const findAllThumbnailsElement = () =>
  document.querySelectorAll(
    '.product-assets-fullscreen-thumbnails .product-assets-fullscreen-thumbnails-item',
  )

const findActiveThumbnailElement = () =>
  document.querySelector(
    '.product-assets-fullscreen-thumbnails .product-assets-fullscreen-thumbnails-item.active',
  )

const findThumbnailBySwiperSlideIndexElement = (index: number) =>
  document.querySelector(
    `.product-assets-fullscreen-thumbnails .product-assets-fullscreen-thumbnails-item[data-swiper-slide-id="${index}"]`,
  )

const onClickThumbnail = (el: Element, swiper: SwiperType) => {
  if (el instanceof HTMLElement) {
    const slideId = Number(el.dataset.swiperSlideId)
    swiper.slideToLoop(slideId)
    findActiveThumbnailElement()?.classList.remove('active')
    el.classList.add('active')
  }
}

const initThumbnailsEvents = (
  swiper: SwiperType,
  assets: AssetType[],
  assetToPreview: AssetType,
) => {
  findAllThumbnailsElement().forEach((el) => {
    el.addEventListener('click', () => onClickThumbnail(el, swiper))
  })

  const indexOfAssetToPreview = assets.findIndex((asset) => asset.id === assetToPreview.id)
  findThumbnailBySwiperSlideIndexElement(indexOfAssetToPreview)?.classList.add('active')
}

const initZoomControlsEvents = (
  swiper: SwiperType,
  panzoomInstances: MutableRefObject<Map<HTMLElement, PanzoomInstance>>,
) => {
  const slider = document.querySelector(
    '.product-assets-fullscreen-zoom-controls-slider',
  ) as HTMLInputElement
  if (slider) {
    slider.addEventListener('input', () =>
      onZoomControlSliderChange(swiper, slider, panzoomInstances),
    )
  }
}

const onZoomControlSliderChange = (
  swiper: SwiperType,
  slider: HTMLInputElement,
  panzoomInstances: MutableRefObject<Map<HTMLElement, PanzoomInstance>>,
) => {
  const scale = Number(slider.value) / 100

  const currentSlide = swiper.slides[swiper.activeIndex]

  const panzoomData = panzoomInstances.current.get(currentSlide)
  if (panzoomData && panzoomData.panzoom) {
    panzoomData.panzoom.zoom(scale, { animate: false })

    const options = panzoomData.panzoom.getOptions()
    const minScale = options.minScale ?? 1
    const epsilon = 0.01
    const imageContainer = currentSlide.querySelector('.zoomable-image') as HTMLElement
    if (Math.abs(scale - minScale) < epsilon) {
      swiper.allowTouchMove = true
      currentSlide.classList.remove('swiper-no-swiping')
      imageContainer.classList.add('product-assets-fullscreen-image--pointer-events-none')
    } else {
      swiper.allowTouchMove = false
      currentSlide.classList.add('swiper-no-swiping')
      imageContainer.classList.remove('product-assets-fullscreen-image--pointer-events-none')
    }
  }
}

const updateZoomControls = (scale: number) => {
  const slider = document.querySelector(
    '.product-assets-fullscreen-zoom-controls-slider',
  ) as HTMLInputElement
  if (slider) {
    slider.value = `${Math.round(scale * 100)}`
  }
}

const initSwiperEvents = (
  swiper: SwiperType,
  assets: AssetType[],
  panzoomInstances: MutableRefObject<Map<HTMLElement, PanzoomInstance>>,
) => {
  swiper.on('slideChange', (instance) => {
    const currentAsset = assets[instance.realIndex]
    findActiveThumbnailElement()?.classList.remove('active')
    findThumbnailBySwiperSlideIndexElement(instance.realIndex)?.classList.add('active')
    showOrHideZoomControls(currentAsset)

    const previousSlide = swiper.slides[swiper.previousIndex]
    const previousPanzoomData = panzoomInstances.current.get(previousSlide)
    if (previousPanzoomData && previousPanzoomData.panzoom) {
      previousPanzoomData.panzoom.reset({ animate: false })
      previousSlide.classList.remove('swiper-no-swiping')
      const previousImageContainer = previousSlide.querySelector('.zoomable-image') as HTMLElement
      previousImageContainer.classList.add('product-assets-fullscreen-image--pointer-events-none')
    }

    const currentSlide = swiper.slides[swiper.activeIndex]
    const currentPanzoomData = panzoomInstances.current.get(currentSlide)
    if (currentPanzoomData && currentPanzoomData.panzoom) {
      const scale = currentPanzoomData.panzoom.getScale()
      updateZoomControls(scale)

      const options = currentPanzoomData.panzoom.getOptions()
      const minScale = options.minScale ?? 1
      const epsilon = 0.01
      const currentImageContainer = currentSlide.querySelector('.zoomable-image') as HTMLElement
      if (Math.abs(scale - minScale) < epsilon) {
        currentSlide.classList.remove('swiper-no-swiping')
        swiper.allowTouchMove = true
        currentImageContainer.classList.add('product-assets-fullscreen-image--pointer-events-none')
      } else {
        currentSlide.classList.add('swiper-no-swiping')
        swiper.allowTouchMove = false
        currentImageContainer.classList.remove(
          'product-assets-fullscreen-image--pointer-events-none',
        )
      }
    } else {
      updateZoomControls(1)
      currentSlide.classList.remove('swiper-no-swiping')
      swiper.allowTouchMove = true
      const currentImageContainer = currentSlide.querySelector('.zoomable-image') as HTMLElement
      currentImageContainer.classList.add('product-assets-fullscreen-image--pointer-events-none')
    }
  })
}

const initPanzoom = (swiper: SwiperType): Map<HTMLElement, PanzoomInstance> => {
  const panzoomInstances = new Map<HTMLElement, PanzoomInstance>()

  swiper.slides.forEach((slide) => {
    const imageContainer = slide.querySelector('.zoomable-image') as HTMLElement
    if (imageContainer) {
      const panzoom = Panzoom(imageContainer, {
        maxScale: 3,
        minScale: 1,
        cursor: 'move',
      })

      const wheelHandler: EventListener = (event) => {
        const wheelEvent = event as WheelEvent
        panzoom.zoomWithWheel(wheelEvent, { step: -0.2 })
      }
      imageContainer.parentElement?.addEventListener('wheel', wheelHandler)

      const zoomHandler: EventListener = (event) => {
        const customEvent = event as CustomEvent<{ scale: number }>
        const scale = customEvent.detail.scale
        updateZoomControls(scale)

        if (scale == 1) {
          panzoom.pan(0, 0, { animate: false })
          imageContainer.classList.add('product-assets-fullscreen-image--pointer-events-none')
        } else {
          imageContainer.classList.remove('product-assets-fullscreen-image--pointer-events-none')
        }
      }

      imageContainer.addEventListener('panzoomzoom', zoomHandler)

      panzoomInstances.set(slide, {
        panzoom,
        wheelHandler,
        zoomHandler,
      })
    }
  })

  return panzoomInstances
}

const useProductAssetsFullScreenSwiper = (assets: AssetType[], assetToPreview: AssetType) => {
  const panzoomInstances = useRef<Map<HTMLElement, PanzoomInstance>>(new Map())

  useIsomorphicLayoutEffect(() => {
    const swiper = new Swiper(
      '.product-assets-fullscreen .product-assets-fullscreen-carousel-swiper',
      {
        modules: [Navigation],
        spaceBetween: 8,
        slidesPerView: 1,
        initialSlide: assets.findIndex((asset) => asset.id === assetToPreview.id),
        navigation: {
          prevEl: '.product-assets-fullscreen .custom-arrow-navigation-left',
          nextEl: '.product-assets-fullscreen .custom-arrow-navigation-right',
        },
        loop: true,
        touchStartPreventDefault: false,
        passiveListeners: false,
      },
    )

    panzoomInstances.current = initPanzoom(swiper)
    initSwiperEvents(swiper, assets, panzoomInstances)
    initThumbnailsEvents(swiper, assets, assetToPreview)
    initZoomControlsEvents(swiper, panzoomInstances)

    return () => {
      try {
        panzoomInstances.current.forEach(({ panzoom, wheelHandler, zoomHandler }, slide) => {
          panzoom.destroy()
          const imageContainer = slide.querySelector('.zoomable-image') as HTMLElement
          if (imageContainer) {
            imageContainer.parentElement?.removeEventListener('wheel', wheelHandler)
            imageContainer.removeEventListener('panzoomzoom', zoomHandler)
          }
        })
        swiper.destroy?.()
      } catch (error) {
        handleError(error, false)
      }
    }
  }, [assets, assetToPreview])
}

export default useProductAssetsFullScreenSwiper
